import * as React from "react";
import createApiClient, {
  apiClient as restoredApiClient,
} from "./createApiClient";
import { toast } from "react-hot-toast";
import { useQuery } from "react-query";
import { validateForm } from "./form-utils";
import settingForm from "../components/settingsForm-data";
import { AWSFile } from "../components/upload/test-component";

type RequestMetaData = {
  loading?: boolean;
  success?: boolean;
  error?: boolean;
};
interface ResponseTuple extends Array<any | RequestMetaData> {
  0: any;
  1: RequestMetaData;
}

export enum UserType {
  Commesso = 0,
  Asseveratore = 1,
  Asseveratore_tecnico = 2,
}

export type User = {
  id: string | number;
  type: UserType;
  approved_budget?: number;
  budget_history?: string;
  tipo_persona?: number;
  name?: string;
  username?: string;
  cf?: number;
  email: string;
  indirizzo?: string;
  cod_destinatario?: string;
  pec?: string;
  tel?: string;
  rap_leg_nome?: string;
  rap_leg_cf?: string;
  rap_leg_email?: string;
  rap_leg_cel?: number;
  ci_fronte?: string;
  ci_retro?: string;
  cf_fronte?: string;
  cf_retro?: string;
  visura?: string;
  status?: number;
  d_i?: string;
  d_m?: string;
  totale_pratiche?: number;
  num_pratiche?: number;
  certified?: number;
  asseveratore?: number;
  id_punto_vendita?: number;
  nome_punto_vendita?: string;
  Token?: string;
};

interface IUserContext {
  loginWithPin?: ({
    auth_pin,
    auth_token,
    remember,
  }: {
    auth_pin: string;
    auth_token: string;
    remember: boolean;
  }) => Promise<User>;
  login?: ({
    email,
    pwd,
    remember,
  }: {
    email: string;
    pwd: string;
    remember: boolean;
  }) => Promise<User>;
  createPin?: ({
    username,
    pwd,
  }: {
    username: string;
    pwd: string;
  }) => Promise<any>;
  logout: () => void;
  user?: User;
}

const apiClient = createApiClient();

export const userContext = React.createContext<IUserContext>({
  logout: () => null,
});

export function useUser() {
  return React.useContext(userContext);
}

function storeUser(user: User, temp: boolean) {
  if (temp) {
    window.sessionStorage.setItem("quser", JSON.stringify(user));
  } else {
    window.localStorage.setItem("quser", JSON.stringify(user));
  }
}

export function restoreUser() {
  const data =
    window.localStorage.getItem("quser") ||
    window.sessionStorage.getItem("quser");

  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch (err) {
      console.warn("No user.");
    }
  }
}

function unstoreUser() {
  window.localStorage.removeItem("quser");
  window.sessionStorage.removeItem("quser");
}

export function UserContext({ children }: { children: JSX.Element }) {
  const [user, setUser] = React.useState(() => restoreUser());

  const contextVal = React.useMemo<IUserContext>(
    () => ({
      createPin: async ({ username, pwd }) => {
        const res: any = await apiClient.post("/login", {
          username,
          secret: pwd,
        });
        return res;
        //api per la creazione del pin ritorno authToken
      },
      loginWithPin: async ({ auth_pin, auth_token, remember }) => {
        //come api login ma aggiungere PIN e authToken
        const user: Record<string, any> = await apiClient.post("/mfa_login", {
          auth_pin,
          auth_token,
        });

        const formedUser = {
          ...user,
          type: parseInt(user.type, 10) as UserType,
        } as User;

        storeUser(formedUser, !remember);
        setUser(formedUser);

        return formedUser;
      },
      login: async ({ email, pwd, remember }) => {
        const user: Record<string, any> = await apiClient.post("/login", {
          email,
          secret: pwd,
        });

        const formedUser = {
          ...user,
          type: parseInt(user.type, 10) as UserType,
        } as User;

        storeUser(formedUser, !remember);
        setUser(formedUser);

        return formedUser;
      },
      logout: () => {
        unstoreUser();
        setUser(undefined);
      },
      user,
    }),
    [user, setUser]
  );

  return (
    <userContext.Provider value={contextVal}>{children}</userContext.Provider>
  );
}

export function useRequestNewContract(
  id: string | number,
  file?: AWSFile
): ResponseTuple {
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState(false);

  const requestNewContract = React.useCallback(async () => {
    if (!file) return;
    setError(false);
    setSuccess(false);
    setLoading(true);
    try {
      await restoredApiClient.post("/user/set_budget", {
        id,
        budget: 200000,
        file: {
          key: file.Key,
          bucket: file.Bucket,
        },
      });
      setSuccess(true);
      toast.success("Richiesta Modifiche inviata con successo.");
    } catch (err) {
      setError(true);
      toast.error(`${err}`.replace("Error:", ""));
    }
    setLoading(false);
  }, [id, file]);

  return [requestNewContract, { loading, success, error }];
}

export function useSaveSettings(): ResponseTuple {
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState(false);

  const saveSettings = React.useCallback(
    async (values: any) => {
      setError(false);
      setSuccess(false);
      setLoading(true);
      if (!validateForm(values, settingForm)) {
        setError(true);
        return false;
      }
      try {
        await restoredApiClient.post("/user/update", values);
        setSuccess(true);
        toast.success("Richiesta modifiche inviata con successo.");
      } catch (err) {
        setError(true);
        toast.error(`${err}`.replace("Error:", ""));
      }
      setLoading(false);
      return true;
    },
    [setError, setSuccess, setLoading]
  );

  return [saveSettings, { loading, success, error }];
}

export function useUpdatePassword(): ResponseTuple {
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState(false);

  const updatePassword = React.useCallback(
    async (oldPassword: string, newPassword: string, callback: () => void) => {
      setError(false);
      setSuccess(false);
      setLoading(true);
      try {
        await restoredApiClient.post("/user/password/update", {
          old: oldPassword,
          new: newPassword,
        });
        callback?.();
        setSuccess(true);
        toast.success("La password è stata aggiornata con successo.");
      } catch (err) {
        setError(true);
        toast.error(`${err}`.replace("Error:", ""));
      }
      setLoading(false);
    },
    [setError, setSuccess, setLoading]
  );

  return [updatePassword, { loading, success, error }];
}

async function fetchUser(): Promise<{ items?: User[] }> {
  return await restoredApiClient.post("/users");
}

export function useSelfBudgetData(): ResponseTuple {
  const { data, isLoading } = useQuery(["my-user-data"], fetchUser);
  const {
    approved_budget,
    totale_pratiche,
    num_pratiche,
  } = React.useMemo(() => {
    const { approved_budget, totale_pratiche, num_pratiche } =
      data?.items?.[0] || {};
    return {
      approved_budget: approved_budget || 0,
      totale_pratiche: totale_pratiche || 0,
      num_pratiche: num_pratiche || 0,
    };
  }, [data]);

  return [
    {
      approved_budget,
      totale_pratiche,
      num_pratiche,
      isWarning: approved_budget - totale_pratiche < 1000,
    },
    { loading: isLoading },
  ];
}
